import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AssetDto, Tag, UpdateTagBody } from '@nusign/picflow-backend-types';
import { Observable, Subject, catchError, of, switchMap, tap } from 'rxjs';
import { IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { GetAllService } from './get-all.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root',
})
export class TagService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private observableLoader = inject(ObservableDataLoaderService);
  private getAllService = inject(GetAllService);
  private socket = inject(SocketService);

  private onTagCreated$ = new Subject<Tag>();
  private onTagCreated = this.onTagCreated$.subscribe(tag => {
    this.observableLoader.update<Tag[]>('tags', tags => {
      if (!tags) return;
      if (tags.find(_tag => _tag.id === tag.id)) return tags;
      return [...tags, tag];
    });
  });

  private onTagUpdated$ = new Subject<Tag>();
  private onTagUpdated = this.onTagUpdated$.subscribe(tag => {
    this.observableLoader.update<Tag[]>('tags', tags => (tags || []).map(_tag => (_tag.id === tag.id ? tag : _tag)));
  });

  private onTagDeleted$ = new Subject<Tag>();
  private onTagDeleted = this.onTagDeleted$.subscribe(tag => {
    this.observableLoader.update<Tag[]>('tags', tags => (tags || []).filter(_tag => _tag.id !== tag.id));
  });

  constructor() {
    this.socket.onSocketEvent.subscribe(event => {
      if (!event) return;
      if (event.name === 'tag.deleted') {
        this.onTagDeleted$.next(event.payload.tag as Tag);
      }
      if (event.name === 'tag.updated') {
        this.onTagUpdated$.next(event.payload.tag as Tag);
      }
      if (event.name === 'tag.created') {
        this.onTagCreated$.next(event.payload.tag as Tag);
      }
    });
  }

  tags$ = of(null).pipe(
    switchMap(() =>
      this.observableLoader.get(
        'tags',
        () => {
          return this.getAllService
            .getAll<Tag>(`${this.config.apiEndpoint}/tags`, new HttpContext().set(IGNORE_AUTH_ERRORS, true))
            .pipe(
              catchError(err => {
                console.error(err);
                // Returning an empty array here will prevent the loader from trying to load the data again
                return of([]) as Observable<Tag[]>;
              })
            );
        },
        1000 * 60 * 2
      )
    )
  );

  public createTag(name: string, category: string) {
    return this.http.post<Tag>(`${this.config.apiEndpoint}/tags`, { name, category }).pipe(
      tap(resp => {
        this.observableLoader.update<Tag[]>('tags', tags => {
          if (!tags) return;
          return [...tags, resp];
        });
      })
    );
  }

  public updateTag(tagId: string, params: UpdateTagBody) {
    return this.http.patch<Tag>(`${this.config.apiEndpoint}/tags/${tagId}`, params).pipe(
      tap(resp => {
        this.observableLoader.update<Tag[]>('tags', tags => (tags || []).map(tag => (tag.id === tagId ? resp : tag)));
      })
    );
  }

  public deleteTag(tagId: string) {
    return this.http.delete(`${this.config.apiEndpoint}/tags/${tagId}`).pipe(
      tap(() => {
        this.observableLoader.update<Tag[]>('tags', tags => (tags || []).filter(tag => tag.id !== tagId));
        this.observableLoader.updateFuzzy<AssetDto[] | AssetDto>('assets_', assets => {
          if (!assets) return;

          if (Array.isArray(assets)) {
            assets.forEach(asset => {
              asset.tags = asset.tags.filter(tag => tag.id !== tagId);
            });
          } else {
            assets.tags = assets.tags.filter(tag => tag.id !== tagId);
          }

          return assets;
        });
      })
    );
  }
}
